import { Injectable } from '@angular/core';

import { Observable, EMPTY } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { isPresent, isTruthy } from '@fcom/utils';
import { Amount } from '@fcom/dapi';

import {
  ABBR_SETTINGS,
  Abbreviation,
  CurrencySettings,
  CustomAbbreviationSettings,
  DEFAULT_ABBR_SETTINGS,
  MAX_ABBR_DIGITS,
  currencySettings,
} from './currency-settings';

const rounder = {
  1: 1,
  2: 10,
  3: 100,
  4: 1000,
};

const DEFAULT_SETTINGS: CurrencySettings = {
  stripZeroCents: false,
  roundDigits: 1,
};

@Injectable()
export class CurrencyService {
  constructor(private languageService: LanguageService) {}

  formatPrice(
    price: Amount,
    roundPrice = false,
    useSymbol = true,
    settings: CurrencySettings = {},
    useAbsoluteValue = false,
    abbreviate = false
  ): Observable<string> {
    if (!isTruthy(price) || !isTruthy(price.amount) || !isTruthy(price.currencyCode)) {
      return EMPTY;
    }
    const mergedSettings = Object.assign({}, DEFAULT_SETTINGS, currencySettings[price.currencyCode], settings);
    // eslint-disable-next-line no-sparse-arrays
    const [, plusSign] = price.amount.match(/(^\+)\d{1,}/) || [, ''];

    const priceNumber = useAbsoluteValue ? Math.abs(Number(price.amount)) : Number(price.amount);

    let amount: string = priceNumber.toFixed(2);
    if (roundPrice && mergedSettings.roundDigits) {
      amount =
        plusSign +
        (Math.ceil(priceNumber / rounder[mergedSettings.roundDigits]) * rounder[mergedSettings.roundDigits]).toString();
    }
    if (mergedSettings.stripZeroCents) {
      amount = plusSign + amount.replace(/\.0{1,3}$/, '');
    }
    return this.languageService.translate('number').pipe(
      map((i18n) => {
        const code =
          useSymbol &&
          isPresent(mergedSettings.symbol) &&
          (!isPresent(mergedSettings?.customAbbrSettings?.symbol) || !abbreviate)
            ? mergedSettings.symbol
            : '';

        if (abbreviate) {
          const { value, abbreviated } = this.abbreviateAmount(
            Number(amount).toFixed(0),
            i18n.thousandAbbreviation,
            i18n.millionAbbreviation,
            mergedSettings?.customAbbrSettings
          );

          const shouldSetSymbol = !abbreviated && !(useSymbol && isPresent(mergedSettings?.symbol));
          const symbol = shouldSetSymbol
            ? useSymbol && isPresent(mergedSettings?.calendarOptions?.symbol)
              ? mergedSettings?.calendarOptions?.symbol
              : price.currencyCode
            : '';
          const outputString = i18n.currencyFormat.replace(/1/, value).replace(/\s/g, '');
          return outputString.replace(/\$/, code).replace(/XYZ/, symbol).trim();
        }

        const outputAmount = amount
          .replace('.', i18n.decimalSeparator)
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${i18n.thousandSeparator}`);
        const symbol = useSymbol && isPresent(mergedSettings?.symbol) ? '' : price.currencyCode;
        const outputString = i18n.currencyFormat.replace(/1/, outputAmount);

        return outputString.replace(/\$/, code).replace(/XYZ/, symbol).trim();
      })
    );
  }

  formatPoints(amount: number, abbreviate = false): Observable<string> {
    return this.languageService.translate('number').pipe(
      switchMap((i18n) =>
        this.formatPrice(
          {
            amount: amount.toString(),
            currencyCode: i18n.currency,
          },
          false,
          false,
          { stripZeroCents: true },
          false,
          abbreviate
        ).pipe(map((points) => points.replace(i18n.currency, '').trim()))
      )
    );
  }

  private abbreviateAmount(
    amount: string,
    thousandAbbr: string,
    millionAbbr: string,
    customAbbrSettings: CustomAbbreviationSettings
  ): { value: string; abbreviated?: boolean } {
    if (!isPresent(amount) || amount.length <= 4) {
      return { value: amount, abbreviated: false };
    }

    const { divider, decimals }: Abbreviation = isPresent(customAbbrSettings)
      ? customAbbrSettings.settings[amount.length] || customAbbrSettings.settings[MAX_ABBR_DIGITS]
      : ABBR_SETTINGS[amount.length] || DEFAULT_ABBR_SETTINGS;

    const basicAbbr: string =
      amount.length < MAX_ABBR_DIGITS || (isPresent(customAbbrSettings) && this.log10(divider) < MAX_ABBR_DIGITS)
        ? thousandAbbr
        : millionAbbr;
    const abbreviation: string =
      isPresent(customAbbrSettings) && customAbbrSettings?.useSymbol.includes(this.languageService.langValue)
        ? customAbbrSettings.symbol
        : basicAbbr;
    const value = `${(Number(amount) / divider).toFixed(decimals).replace(/\.0{1,3}$/, '')}${abbreviation}`;

    return { value, abbreviated: true };
  }

  private log10(divider: number): number {
    return Math.log(divider) / Math.log(10);
  }
}
